import React, { useEffect, useState } from 'react';
import { Form, Field } from "react-final-form";
import { FormControlLabel, Checkbox } from '@mui/material';
import {
  Box,
  Typography,
  Tooltip,
  CircularProgress,
  Button,
} from "@mui/material";
import {
  RenderTypography,
} from "../../../../Forms/FormFields";
import { getSppDisplayName } from "../../../../../utils/caseConvert";
import ImageContainer from "./ImageContainer";
import AutoSave from "./AutoSave";
import _ from "lodash";
import { capitalize } from "../../../../../utils/caseConvert";
import SpecimenFormLayout from './SpecimenFormLayout';
import VerifyCheckBox from './Components/CheckBox'

const specimenSx = {
  fontSize: [".75rem", ".75rem", ".95rem", ".95rem", "1.125rem", "1.125rem"],
};

const smallerSpecimenSx = {
  fontSize: [".5rem", ".5rem", ".75rem", ".75rem", "1rem", "1rem"],
};

const SpecimenDetails = (props) => {
  const {
    image,
    backImage,
    frontImage,
    trayImage,
    specimenNum,
    lastSpecimenNum,
    handleNextSpecimen,
    handlePreviousSpecimen,
    algGenus,
    algSpecies,
    algSpeciesConfidence,
    algRegional,
    algSex,
    nrAlgGenus,
    nrAlgSpecies,
    nrAlgSpeciesConfidence,
    nrAlgRegional,
    userAlgConfidence,
    specimenID,
    defaultInstitutionId,
    trayId,
    updatingDisplayed,
    // eslint-disable-next-line
    loading,
    hideFormErrors,
    metadata,
    shouldAllowAutofill,
    plateAutoCompleteOptions,
    autofillModalOpen,
    setAutofillModalOpen,
    handleAutofill,
    loadingAlgResults,
    onBlurGenusSpecies,
    onBlurPlate,
    onBlurWell,
    normalizeGenusSpecies,
    normalizeWell,
    wellAutocompleteOptions,
    autofillSettings,
    tagOptions,
    specimenValues,
    saveCurrentChange,
    flipTraySide,
    isTwoSideImaged,
    vectorType,
  } = props;
  const [autofillTooltip, setAutofillTooltip] = useState(false);
  const [highLightSpecies, setHighLightSpecies] = useState(false);
  const [highLightSex, setHighLightSex] = useState(false);
  const [highLightPlateAndWell, setHighLightPlateAndWell] = useState(false);
  const [highLightLifeStage, setHighLightLifeStage] = useState(false);
  const [highLightGonotrophic, setHighLightGonotrophic] = useState(false);

  const handleCheckboxChange = (e, spp) => {
    const checked = e.target.checked;
    let toSaveSpp = checked ? spp : "";
    saveCurrentChange(
      {
        ...specimenValues,
        genusSpecies: toSaveSpp
      },
      { genusSpecies: toSaveSpp },
      false
    );
  };

  const handleSexCheckboxChange = (e, sex) => {
    const checked = e.target.checked;
    let toSaveSex = checked ? sex : null;
    saveCurrentChange(
      {
        ...specimenValues,
        sex: toSaveSex
      },
      { sex: toSaveSex },
      false
    );
  };

  useEffect(() => {
    setHighLightSpecies(
      autofillTooltip &
      autofillSettings?.speciesChecked &
      !(specimenValues?.genusSpecies?.length > 0)
    );
    setHighLightPlateAndWell(
      autofillTooltip &
      autofillSettings?.plateAndWellChecked &
      !(
        (specimenValues?.plate?.length > 0) &
        (specimenValues?.well?.length > 0)
      )
    );
    setHighLightSex(
      autofillTooltip &
      autofillSettings?.sexChecked &
      !(specimenValues?.sex?.length > 0)
    );
    setHighLightLifeStage(
      autofillTooltip &
      autofillSettings?.lifeStageChecked &
      !(specimenValues?.lifeStage?.length > 0)
    );
    setHighLightGonotrophic(
      autofillTooltip &
      autofillSettings?.gonotrophicChecked &
      !(specimenValues?.gonotrophic?.length > 0)
    );
  }, [autofillTooltip, autofillSettings, specimenValues]);

  const clearToolTip = () => {
    setAutofillTooltip(false);
    setHighLightSpecies(false);
    setHighLightSex(false);
    setHighLightPlateAndWell(false);
    setHighLightLifeStage(false);
    setHighLightGonotrophic(false);
  };

  const save = async (values, difference) => {
    let isSpecimenChanged = difference.id ? true : false;
    saveCurrentChange(values, difference, isSpecimenChanged);
  };

  const computeUnknownList = () => {
    let unknownList = [];
    const inRegionName = getSppDisplayName(algGenus, algSpecies);
    const inRegionConf = Math.round(algSpeciesConfidence * 100);
    if (inRegionName && inRegionConf) {
      unknownList.push({
        name: inRegionName,
        genus: algGenus,
        species: algSpecies,
        confidence: inRegionConf,
        region:
          algRegional && nrAlgRegional
            ? ""
            : algRegional
              ? " (in region)"
              : " (out of region)",
      });
    }

    if (nrAlgGenus && nrAlgSpecies && nrAlgGenus !== "") {
      const nrName = getSppDisplayName(nrAlgGenus, nrAlgSpecies);
      const nrConf = Math.round(nrAlgSpeciesConfidence * 100);
      if (nrName && nrConf) {
        unknownList.push({
          name: nrName,
          genus: nrAlgGenus,
          species: nrAlgSpecies,
          confidence: nrConf,
          region:
            algRegional && nrAlgRegional
              ? ""
              : nrAlgRegional
                ? " (in region)"
                : " (out of region)",
        });
      }
    }
    return unknownList
      .sort((a, b) => b.confidence - a.confidence)
      .map((cur, index) => {
        return (
          <Typography
            sx={{ ...smallerSpecimenSx, width: '400px', }}
            key={index}
            onClick={(e) => {
              saveCurrentChange(
                { ...specimenValues, genusSpecies: cur.name },
                { genusSpecies: cur.name },
                false
              );
            }}
          >
            <em>{capitalize(cur.name)}</em> - {`${cur.confidence}%`}
            {cur.region}
            <FormControlLabel
              control={
                <Tooltip title="Verify Specimen Algorithm" arrow>
                  <Checkbox
                    checked={specimenValues?.genusSpecies === cur.name}
                    onChange={(e) => handleCheckboxChange(e, cur.name)}
                    size="small"
                    sx={{
                      marginTop:
                        [
                          '-0.3rem',
                          '-0.3rem',
                          '-0.3rem',
                          '-0.3rem',
                          '-0.2rem', '-0.2rem',
                        ],
                      color: specimenValues?.genusSpecies === cur.name ? 'green' : 'primary',
                      '&.Mui-checked': {
                        color: 'green',
                      },
                    }}
                  />
                </Tooltip>
              }
              label={""}
              sx={{ ml: 0.05 }}
            />
          </Typography>
        );
      });
  };


  const validator = (values) => {
    const errors = {};
    if (
      values.well &&
      values.well?.length > 0 &&
      (values.plate?.length <= 0 || !values.plate)
    ) {
      errors.plate = "Must have a plate number";
    }
    if (
      values.plate &&
      values.plate?.length > 0 &&
      (values.well?.length <= 0 || !values.well)
    ) {
      errors.well = "Must have a well number";
    }
    return errors;
  }

  return (
    <Form
      onSubmit={() => { }}
      mutators={{
        setSex: (args, state, utils) => {
          utils.changeValue(state, "sex", () => "");
        },
        setValue: ([field, value], state, { changeValue }) => {
          changeValue(state, field, () => value);
        },
      }}
      initialValues={specimenValues}
      validate={validator}
      render={(form, change) => (
        <div className={"flex flex-col"} id={"specimen-details"}>
          <AutoSave debounce={300} save={save} form={form} />
          <Box
            className={"flex mx-auto justify-between"}
            sx={{
              width: ["250px", "250px", "250px", "250px", "300px", "300px"],
              marginTop: ["3rem", "3rem", "3rem", "3rem", "1rem", "1rem"],
              maxWidth: "100%",
            }}
          >
            <span id={"specimen-id"} className={" text-blue-400 my-auto"}>
              <Field
                label="Institution Id"
                name={`institutionId`}
                inputProps={{
                  id: `institution-id-field`,
                  "data-testid": "Institution Id",
                }}
                fieldValue={metadata?.[`institutionId`]}
                hideFormErrors={hideFormErrors}
                normalize={normalizeGenusSpecies}
                component={RenderTypography}
                defaultInstitutionId={defaultInstitutionId}
              />
            </span>
            {isTwoSideImaged && (
              <Button size="small" variant="outlined" onClick={flipTraySide}>
                Flip
              </Button>
            )}
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Typography
                id={"specimen-position"}
                className={" text-blue-400"}
                sx={{
                  ...specimenSx,
                  textAlign: "center",
                  my: "auto",
                }}
              >
                POS {specimenNum ? specimenNum + 1 : 1}
              </Typography>
            </Box>
          </Box>
          <ImageContainer
            image={image}
            backImage={backImage}
            frontImage={frontImage}
            trayImage={trayImage}
            handleNextSpecimen={() => handleNextSpecimen(form)}
            handlePreviousSpecimen={() => handlePreviousSpecimen(form)}
            specimenID={specimenID}
            trayId={trayId}
            updatingDisplayed={updatingDisplayed}
          />

          <Box
            className={"flex mx-auto justify-between"}
            sx={{
              width: ["250px", "280px", "280px", "280px", "300px", "330px"],
              marginTop: [
                "0.5rem",
                "0.5rem",
                "0.5rem",
                "0.5rem",
                "0.75rem",
                "0.75rem",
              ],
              marginBottom: ["1rem", "1rem", "1rem", "1rem", "1rem", "1rem"],
            }}
          >
            {algSpeciesConfidence || algSpeciesConfidence === 0 ? (
              (algSpeciesConfidence > userAlgConfidence && algRegional) ||
                ["empty", "Empty", "Dirty", "dirty"].includes(algGenus) ? (
                <>
                  <div className={"flex items-center"}>
                    <Typography
                      id={"alg-genus"}
                      className={"italic"}
                      sx={{
                        ...specimenSx,
                        lineHeight: 0.6,
                      }}
                    // onClick={() => {
                    //   saveCurrentChange(
                    //     {
                    //       ...specimenValues,
                    //       genusSpecies: getSppDisplayName(
                    //         algGenus,
                    //         algSpecies
                    //       ),
                    //     },
                    //     {
                    //       genusSpecies: getSppDisplayName(
                    //         algGenus,
                    //         algSpecies
                    //       ),
                    //     },
                    //     false
                    //   );
                    // }}
                    >
                      {(algGenus || algGenus === "") &&
                        (algSpecies || algSpecies === "")
                        ? getSppDisplayName(algGenus, algSpecies)
                        : null
                      }
                      <VerifyCheckBox title="Verify Specimen Algorithm"
                        handleChange={(e) => handleCheckboxChange(e, getSppDisplayName(algGenus, algSpecies))}
                        checked={specimenValues?.genusSpecies === getSppDisplayName(algGenus, algSpecies)}
                      />
                      <br />
                      {["empty", "Empty", "Dirty", "dirty"].includes(algGenus)
                        ? ""
                        : ["male", "female", 'Male', 'Female'].includes(algSex) ?
                          <>{_.capitalize(algSex)}
                            <VerifyCheckBox title="Verify Specimen Sex Algorithm"
                              handleChange={(e) => handleSexCheckboxChange(e, algSex)}
                              checked={specimenValues?.sex === algSex}
                            />
                          </>
                          : null}
                    </Typography>
                    <Typography sx={smallerSpecimenSx}></Typography>
                    {loadingAlgResults && (
                      <CircularProgress size={"1rem"} className={"ml-2"} />
                    )}
                  </div>
                  <div className={"flex items-center"}>
                    {/* <Typography id={"alg-genus-confidence"} sx={specimenSx}>
                      {isNaN(algSpeciesConfidence) ||
                        ((algGenus || algGenus === "") &&
                          (algSpecies || algSpecies === "") &&
                          (algGenus?.toLowerCase() === "unknown" ||
                            algSpeciesConfidence === 0)) ||
                        ["empty", "Empty", "Dirty", "dirty"].includes(algGenus)
                        ? ""
                        : `${Math.round(algSpeciesConfidence * 100)}%`}
                    </Typography> */}
                    {loadingAlgResults && (
                      <CircularProgress size={"1rem"} className={"ml-2"} />
                    )}
                  </div>
                </>
              ) : (
                <div>
                  <Typography sx={specimenSx}>Unknown Species</Typography>
                  <Box sx={{ pl: ".5rem" }}>{computeUnknownList()}</Box>
                  {algSex && (algSex === "male" || algSex === "female") ? (
                    <Box className={"ml-2"}>
                      <Typography sx={smallerSpecimenSx}>
                        {algGenus === "Empty" || algGenus === "empty"
                          ? "Sex Unsure"
                          : algSex === "male"
                            ? "Male"
                            : "Female"}
                        <VerifyCheckBox title="Verify Specimen Sex Algorithm"
                          handleChange={(e) => handleSexCheckboxChange(e, algSex)}
                          checked={specimenValues?.sex === algSex}
                        />
                      </Typography>
                    </Box>
                  ) : null}
                </div>
              )
            ) : null}
          </Box>

          <SpecimenFormLayout
            shouldAllowAutofill={shouldAllowAutofill}
            setAutofillTooltip={setAutofillTooltip}
            clearToolTip={clearToolTip}
            autofillModalOpen={autofillModalOpen}
            setAutofillModalOpen={setAutofillModalOpen}
            form={form}
            specimenNum={specimenNum}
            lastSpecimenNum={lastSpecimenNum}
            specimenValues={specimenValues}
            autofillSettings={autofillSettings}
            autofillTooltip={autofillTooltip}
            handleAutofill={handleAutofill}
            highLightSpecies={highLightSpecies}
            hideFormErrors={hideFormErrors}
            normalizeGenusSpecies={normalizeGenusSpecies}
            vectorType={vectorType}
            onBlurGenusSpecies={onBlurGenusSpecies}
            highLightSex={highLightSex}
            change={change}
            tagOptions={tagOptions}
            highLightLifeStage={highLightLifeStage}
            highLightGonotrophic={highLightGonotrophic}
            highLightPlateAndWell={highLightPlateAndWell}
            plateAutoCompleteOptions={plateAutoCompleteOptions}
            onBlurPlate={onBlurPlate}
            onBlurWell={onBlurWell}
            normalizeWell={normalizeWell}
            wellAutocompleteOptions={wellAutocompleteOptions}
            allSpecimensHaveEmpty={props.allSpecimensHaveEmpty}
          />
        </div>
      )}
    ></Form>
  );
};

export default SpecimenDetails;
